import { useState } from 'react'
import type { PetType } from '../lib/types'

interface SearchFormProps {
  onSearch: (keyword: string, petType: PetType) => void
  isLoading: boolean
}

const petTypes: { value: PetType; label: string; emoji: string }[] = [
  { value: 'dog', label: 'わんちゃん', emoji: '🐕' },
  { value: 'cat', label: 'ねこちゃん', emoji: '🐈' },
  { value: 'other', label: 'その他', emoji: '🐰' },
]

export function SearchForm({ onSearch, isLoading }: SearchFormProps) {
  const [keyword, setKeyword] = useState('')
  const [petType, setPetType] = useState<PetType>('dog')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (keyword.trim()) {
      onSearch(keyword.trim(), petType)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* ペットタイプ選択 */}
      <div>
        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
          <span>🐾</span>
          ペットの種類
        </label>
        <div className="grid grid-cols-3 gap-3">
          {petTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setPetType(type.value)}
              disabled={isLoading}
              className={`
                p-4 rounded-2xl font-medium transition-all duration-300
                flex flex-col items-center gap-2
                ${
                  petType === type.value
                    ? 'bg-gradient-to-br from-light-accent to-pet-coral dark:from-dark-accent dark:to-pet-coral text-white shadow-cute scale-105'
                    : 'bg-light-bg-sub dark:bg-dark-bg-sub text-light-text dark:text-dark-text hover:bg-light-paw/30 dark:hover:bg-dark-paw/20'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              <span className={`text-3xl ${petType === type.value ? 'animate-wiggle' : ''}`}>
                {type.emoji}
              </span>
              <span className="text-sm">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* キーワード入力 */}
      <div>
        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
          <span>💭</span>
          症状キーワード
        </label>
        <div className="relative">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="例: 下痢、嘔吐、元気がない..."
            disabled={isLoading}
            className="
              w-full px-5 py-4 rounded-2xl
              border-2 border-light-paw/30 dark:border-dark-paw/30
              bg-white dark:bg-dark-bg-sub
              text-light-text dark:text-dark-text
              placeholder-light-text-sub/50 dark:placeholder-dark-text-sub/50
              focus:border-light-accent dark:focus:border-dark-accent
              focus:ring-4 focus:ring-light-accent/20 dark:focus:ring-dark-accent/20
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-300
              text-lg
            "
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl opacity-30">
            🔍
          </span>
        </div>
      </div>

      {/* 検索ボタン */}
      <button
        type="submit"
        disabled={isLoading || !keyword.trim()}
        className="
          w-full py-4 px-6 rounded-2xl font-bold text-lg
          bg-gradient-to-r from-light-accent to-light-secondary
          dark:from-dark-accent dark:to-dark-secondary
          text-white shadow-cute
          hover:shadow-cute-lg hover:scale-[1.02]
          active:scale-[0.98]
          disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
          transition-all duration-300
          flex items-center justify-center gap-3
        "
      >
        {isLoading ? (
          <>
            <span className="loading-paw">🐾</span>
            <span className="loading-paw">🐾</span>
            <span className="loading-paw">🐾</span>
            <span className="ml-2">検索中...</span>
          </>
        ) : (
          <>
            <span className="text-2xl">🔍</span>
            検索する
          </>
        )}
      </button>
    </form>
  )
}
